"use client";

import { useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { previewReleaseSelection } from "@/lib/actions/release-schedule-actions";
import type { PublishingRow } from "@/lib/paywall/queries";
import { parsePaywallFilters, type PaywallFilters } from "@/lib/paywall/bulk-access";
import type { ChapterSelection } from "@/lib/paywall/bulk-access";
import { ReleaseSchedulePanel } from "./release-schedule-panel";
import { useControlPreview } from "@/components/editor/use-control-preview";
import { MAX_RELEASE_SELECTION } from "@/lib/releases/schedule";

function scheduleRows(rows: PublishingRow[]) {
    const drafts = rows.filter(row => row.status === "draft");
    if (!drafts.length) throw new Error("Select unpublished chapters");
    if (drafts.length > MAX_RELEASE_SELECTION) throw new Error(`Narrow your selection to ${MAX_RELEASE_SELECTION} chapters`);
    return drafts;
}

export function ReleaseScheduleButton({ storyId, selection, filters, beforeOpen, onSaved, onBusy, children, disabled, label }: {
    storyId: string; selection: ChapterSelection; filters?: PaywallFilters; beforeOpen?: () => Promise<boolean>;
    onSaved?: () => void; onBusy?: (busy: boolean) => void; children: React.ReactNode; disabled?: boolean; label?: string;
}) {
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [rows, setRows] = useState<PublishingRow[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const request = useRef(0);
    const resolvedFilters = filters ?? parsePaywallFilters({});
    const loadRows = async () => {
        const result = await previewReleaseSelection(storyId, resolvedFilters, selection);
        if (result.error) throw new Error(result.error);
        return scheduleRows(result.rows ?? []);
    };
    const loader = useControlPreview({ kind: "release", storyId, filters: resolvedFilters, selection, fromRows: scheduleRows, request: loadRows });
    async function changeOpen(next: boolean) {
        if (saving) return;
        setOpen(next);
        const version = ++request.current;
        if (!next) return;
        setRows(null); setError(null);
        try {
            if (beforeOpen && !(await beforeOpen())) throw new Error("Save your chapter before scheduling its release");
            if (version !== request.current) return;
            // Editor content must be saved before reading its current metadata;
            // hover/focus must never trigger a save or reuse a pre-save preview.
            const result = beforeOpen ? await loadRows() : loader.ready() ?? await loader.load();
            if (version !== request.current) return;
            setRows(result);
        } catch (error) {
            if (version === request.current) setError(error instanceof Error ? error.message : "Unable to load chapters");
        }
    }
    return <Popover open={open} onOpenChange={changeOpen}>
        <PopoverTrigger asChild><Button size="sm" variant="outline" aria-label={label} disabled={disabled || saving} onPointerEnter={() => { if (!beforeOpen && !disabled && !open) loader.prefetch(); }} onFocus={() => { if (!beforeOpen && !disabled && !open) loader.prefetch(); }}>{children}<ChevronDown className="size-3" /></Button></PopoverTrigger>
        <PopoverContent side="bottom" align="end" collisionPadding={16}
            className="w-96 max-w-[calc(100vw-2rem)] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto"
            aria-label="Edit chapter release schedule"
            onInteractOutside={event => { if (saving) event.preventDefault(); }}
            onEscapeKeyDown={event => { if (saving) event.preventDefault(); }}>
            {error ? <p role="alert" className="text-sm text-destructive">{error}</p> : rows ? <ReleaseSchedulePanel
                storyId={storyId} rows={rows} onClose={() => changeOpen(false)}
                onBusy={busy => { setSaving(busy); onBusy?.(busy); }}
                onSaved={() => { loader.clear(); setOpen(false); onSaved?.(); }}
            /> : <p role="status" className="text-sm">Loading selection…</p>}
        </PopoverContent>
    </Popover>;
}
